import React, { useContext } from 'react'
import { WappContext, withWapp } from 'wapplr-react/dist/common/Wapp'
import { copyObject } from 'wapplr/dist/common/utils'

import clsx from 'clsx'

import TextField from 'karrierhub-ui/dist/common/src/components/Input/Text'
import Button from 'karrierhub-ui/dist/common/src/components/Button'
import Paper from 'karrierhub-ui/dist/common/src/components/Paper'
import Typography from 'karrierhub-ui/dist/common/src/components/Typography'

import PostContext from '../Post/context'
import AccountContext from '../Account/context'
import AppContext from '../App/context'

import { HelperTextComponentForTextArea } from './Components/TextArea'
import validations from './validations'

import style from './style.css'

import { defaultComponents } from './defaultComponents'

function getComponentName({ data, propKey }) {
    if (data.componentName) {
        return data.componentName
    }
    if (propKey === 'submit') {
        return 'Button'
    }
    let componentName = 'TextField'
    if (data.schemaType) {
        switch (data.schemaType) {
            case 'String':
                componentName = 'TextField'
                break
            case 'Number':
                componentName = 'NumberField'
                break
            case 'Boolean':
                componentName = 'Checkbox'
                break
            case 'Float':
                componentName = 'NumberField'
                break
            case 'MongoID':
                componentName = 'Posts'
                break
            default:
                componentName = 'TextField'
        }
    }
    if (typeof data.options === 'object' && data.options.length) {
        componentName = 'Select'
    }
    return componentName
}

function generatePropsAndSelectComponent({ propKey, commonProps = {} }) {

    const {
        components,
        formData,
        onSubmit,
        onChange,
        submitRef,
        size,
        formActions,
        messages,
        name,
        getItem
    } = commonProps

    const data = { ...formData[propKey] }

    const componentName = getComponentName({ data, propKey })

    const Component = components[componentName]?.Component || TextField
    const defaultProps = { ...components[componentName]?.props || {} }

    if (componentName === 'Button' && data.label && typeof data.children == 'undefined') {
        data.children = data.label
        delete data.label
    }

    data.propKey = propKey

    const props = Object.keys(defaultProps).reduce(function(a, key) {
        if (key === 'formData') {
            a[key] = formData
        } else if (key === 'formActions') {
            a[key] = formActions
        } else {
            a[key] = (typeof data[key] !== 'undefined') ? data[key] : defaultProps[key]
        }
        return a
    }, {})

    if (props.type === 'submit') {
        props.onClick = onSubmit
        props.effect = ({ actions }) => {
            submitRef.actions = actions
        }
    } else if (componentName) {
        props.onChange = function(e, value) {
            return onChange(e, value, propKey)
        }
    }

    if (
        typeof defaultProps.defaultValue !== 'undefined' &&
        typeof data.defaultValue === 'undefined' &&
        typeof data.default !== 'undefined'
    ) {
        props.defaultValue = data.default
    }

    if (!props.id) {
        props.id = (propKey.startsWith('record.')) ? propKey.split('record.')[1] : propKey
    }

    if (typeof props.label === 'undefined') {
        props.label = propKey.slice(0, 1).toUpperCase() + propKey.slice(1)
    }

    if (data.required && !data.requiredAsteriskDisableShowOnLabel && props.label && typeof props.label === 'string' && props.label.slice(-2) !== ' *') {
        props.label = props.label + ' *'
    }

    props.key = propKey

    if (!data.size) {
        props.size = size
    }

    if ((data.validations || data.required)) {
        props.validation = (value, changedEver) => {

            const r = {}

            const {
                showInitializationErrors = propKey.startsWith('record') && commonProps.showInitializationErrors
            } = props

            if (data.required && !data.validations?.required) {

                const func = validations['required']({
                    required: data.required,
                    showInitializationErrors,
                    messages,
                    name,
                    getItem,
                    propKey,
                    data,
                    props
                })
                r['required'] = func(value, changedEver)
            }

            if (data.validations) {
                Object.keys(data.validations).forEach((key) => {
                    const validationProps = data.validations[key]
                    if (validations[key]) {
                        const func = validations[key]({
                            required: data.required,
                            showInitializationErrors,
                            ...validationProps,
                            messages,
                            name,
                            getItem,
                            propKey,
                            data,
                            props
                        })
                        r[key] = func(value, changedEver)
                    } else if (typeof data.validations[key] === 'function') {
                        const func = data.validations[key]({
                            required: data.required,
                            showInitializationErrors,
                            messages,
                            name,
                            getItem,
                            propKey,
                            data,
                            props
                        })
                        r[key] = func(value, changedEver)
                    }
                })
            }

            const error = Object.keys(r).find((key) => r[key].error)

            if (error) {
                return r[error]
            }

            return { error: false, errorMessage: '' }

        }

        if (data.validations?.lengthText && !data.validations?.lengthText.disableCounter) {
            props.HelperTextComponent = React.forwardRef((props, ref) => {
                return <HelperTextComponentForTextArea {...props} messages={messages} name={name} ref={ref} />
            })
        }

    }

    return { props, Component }

}

const FormItem = React.forwardRef((props, ref) => {

    const { commonProps = {}, propKey } = props
    const { formData, post, isAdmin, isAuthorOrAdmin, isQuery } = commonProps

    const writeCondition = formData[propKey].writeCondition
    const canWriteAdmin = (writeCondition === 'admin')
    const canWriteAuthorOrAdmin = (post?._id && !canWriteAdmin)
    const canWriteEverybody = (!post?._id && !canWriteAdmin)

    const go = (
        isQuery ||
        (canWriteEverybody) ||
        (canWriteAuthorOrAdmin && isAuthorOrAdmin) ||
        (canWriteAdmin && isAdmin)
    )

    if (!formData[propKey].hidden && go) {

        const componentAndProps = generatePropsAndSelectComponent({
            propKey,
            commonProps
        })

        const Component = componentAndProps.Component
        const componentProps = componentAndProps.props

        return <Component
            {...componentProps}
            effect={(e) => {
                commonProps.setItem(propKey, e)
                if (componentProps.effect) {
                    componentProps.effect(e)
                }
            }}
            ref={ref}
        />

    }

    return null
})

const ContainerRoot = (p) => {

    const { name, container = {}, children, ...props } = p

    const {
        hiddenContainer = (name === 'record' || !name),
        Component = hiddenContainer ? 'div' : Paper,
        label = '',
        LabelProps = {},
        LabelComponent = Typography,
        afterChildren = null
    } = container

    return (
        <>
            {
                <Component
                    {...props}
                    name={name}
                    className={clsx(
                        style.container,
                        { [style.paper]: Component === Paper }
                    )}
                >
                    {label ?
                        <LabelComponent
                            variant={'h5'}
                            weight={600}
                            {...LabelProps}
                            className={clsx(
                                style.label,
                                LabelProps.className
                            )}
                        >
                            {label}
                        </LabelComponent>
                        : null
                    }
                    {children}
                    {afterChildren}
                </Component>
            }
        </>
    )

}

function Container(props) {

    const { container = {}, commonProps } = props

    return (
        <>
            {
                (container.items) ?
                    (Object.keys(container.items).length) ?
                        <ContainerRoot
                            key={container.propKey}
                            name={container.propKey}
                            container={container}
                        >
                            {
                                Object.keys(container.items).map((key) => {
                                    return (
                                        <Container
                                            key={container.items[key].propKey}
                                            container={container.items[key]}
                                            commonProps={commonProps}
                                        />
                                    )
                                })
                            }
                        </ContainerRoot>
                        : null
                    :
                    <FormItem
                        key={container.propKey}
                        commonProps={commonProps}
                        propKey={container.propKey}
                    />
            }
        </>
    )
}

class Form extends React.Component {
    constructor(props, context) {

        super(props, context)

        this.state = {
            formData: { ...copyObject(props.formData, { keep: ['label', 'children', 'startIcon', 'endIcon', 'Component', 'LabelProps', 'LabelComponent', 'afterChildren', 'NewComponentProps'] }) || {} }
        }

        if (props.initialState) {
            this.state = props.initialState(this.state, props, context)
        }

        this.onChange = this.onChange.bind(this)
        this.onSubmit = this.onSubmit.bind(this)
        this.setNewFormData = this.setNewFormData.bind(this)

        this.addStyle = this.addStyle.bind(this)
        this.removeStyle = null
        this.submitRef = props.submitRef || {}
        this.progress = false
        this.done = false
        this.isUnmounted = false
        this.items = {}
        this._formData = {}

        const { wapp } = context
        if (wapp.target === 'node') {
            this.addStyle()
        }

    }

    componentDidUpdate(prevProps) {
        if (this.props.formData !== prevProps.formData) {
            this._formData = {}
            this.setState({
                formData: { ...copyObject(this.props.formData, { keep: ['label', 'children', 'startIcon', 'endIcon', 'Component', 'LabelProps', 'LabelComponent', 'afterChildren', 'NewComponentProps'] }) || {} }
            })
        }
    }

    componentDidMount() {
        this.addStyle()
        this.isUnmounted = false
    }

    componentWillUnmount() {
        if (this.removeStyle) {
            this.removeStyle()
        }
        this.isUnmounted = true
        this.removeLeaveMessageListener()
    }

    addStyle() {
        if (this.removeStyle) {
            this.removeStyle()
        }
        const { wapp } = this.context
        this.removeStyle = wapp.styles.add(style)
    }

    async onChange(e, value, key) {

        this.addLeaveMessageListener()

        const { onChange } = this.props
        const formData = {
            ...this.state.formData,
            ...this._formData
        }
        const data = { ...formData[key] }

        if (value !== data.value) {

            data.value = value

            data.errorMessage = ''
            data.error = false
            data.errors = []

            const newFormData = {
                ...formData,
                [key]: data
            }

            this._formData = {
                ...formData,
                [key]: data
            }

            /*await this.setState({
                formData: {
                    ...formData,
                    [key]: data
                }
            })*/

            await this.props.appContext.template.current?.actions?.setSnackMessage('')

            if (!this.progress) {
                this.done = false
                if (this.submitRef.actions) {
                    await this.submitRef.actions.setDisabled(false)
                    await this.submitRef.actions.setDone(false)
                }
            }

            if (onChange) {
                onChange(e, key, newFormData)
            }
        }
    }


    isIOs() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    }

    addLeaveMessageListener() {
        if (this.props.showLeaveMessage) {

            this.removeLeaveMessageListener()
            const { appContext, leaveMessage = appContext.messages.leaveWarningMessage } = this.props
            appContext.setShouldLeaveWarning(true)

            if (this.isIOs()) {
                return null
            }

            this.leaveMessageListener = function(e) {
                const message = leaveMessage
                e.returnValue = message
                return message
            }
            window.addEventListener('beforeunload', this.leaveMessageListener)
        }
    }

    removeLeaveMessageListener() {
        if (this.props.showLeaveMessage) {
            const { appContext } = this.props
            appContext.setShouldLeaveWarning(false)
            if (this.leaveMessageListener) {
                window.removeEventListener('beforeunload', this.leaveMessageListener)
            }
        }
    }

    getSendData({ formData, validations = {} }) {

        const props = this.props
        const items = this.items

        const { isQuery, accountContext, postContext } = props

        return Object.keys(formData).reduce(function(a, key) {

            const user = postContext.user || accountContext.user
            const post = postContext.post || accountContext.user
            const author = post?._author?._id || post?._author
            const isAuthor = (user?._id && user?._id === author)
            const isAdmin = user && user._status_isFeatured
            const isAuthorOrAdmin = !!(isAuthor || isAdmin)
            const writeCondition = formData[key].writeCondition
            const canWriteAdmin = (writeCondition === 'admin')
            const canWriteAuthorOrAdmin = (post?._id && !canWriteAdmin)
            const canWriteEverybody = (!post?._id && !canWriteAdmin)

            const go = (
                isQuery ||
                (canWriteEverybody) ||
                (canWriteAuthorOrAdmin && isAuthorOrAdmin) ||
                (canWriteAdmin && isAdmin)
            )

            if (go && !formData[key].isContainer) {

                const data = copyObject(formData[key], { keep: ['label', 'children', 'startIcon', 'endIcon', 'onClick', 'Component', 'LabelProps', 'LabelComponent', 'afterChildren', 'NewComponentProps'] })
                const componentName = getComponentName({ data, key })
                if (key !== 'submit' && data.type !== 'submit') {

                    let parent = a
                    let lastKey = key

                    if (key.match('.')) {
                        const names = key.split('.')
                        if (names.length > 1) {
                            names.forEach(function(name, i) {
                                if (!parent[name]) {
                                    parent[name] = {}
                                }
                                if (i < names.length - 1) {
                                    parent = parent[name]
                                }
                                lastKey = name
                            })
                        }
                    }

                    if (componentName.match('Posts') && data.multiple && data.value && !data.value.length) {
                        data.value = null
                    }

                    if (componentName.match('Cover') && data.multiple && data.value && !data.value.length) {
                        data.value = null
                    }

                    if (componentName.match('MegaMenu') && data.multiple && data.value && !data.value.length) {
                        data.value = null
                    }

                    if (data.refPostType && data.multiple && data.value && !data.value.length) {
                        data.value = null
                    }

                    if (componentName === 'Checkbox' && !data.value) {
                        data.value = false
                    }

                    if (componentName === 'Switch' && !data.value) {
                        data.value = false
                    }

                    function transformRelations(value) {
                        if (value) {
                            if (typeof value === 'object' && typeof value.length === 'number') {
                                return value.map((p) => p._id || p)
                            } else if (value._id) {
                                return value._id
                            } else {
                                return value
                            }
                        }
                    }

                    function getValue() {
                        let value = (
                            data.value ||
                            (data.value === 0 && data.schemaType === 'Number') ||
                            (data.value === 0 && data.schemaType === 'Float') ||
                            (data.value === false && data.schemaType === 'Boolean')
                        ) ?
                            data.value :
                            (data.required) ?
                                ((props.formData[key] ? props.formData[key].default : formData[key].default) || null)
                                : null

                        if ((data.refPostType || componentName.match('MegaMenu') || componentName.match('Posts') || componentName.match('Cover')) && value) {
                            value = transformRelations(value)
                        }

                        return value
                    }

                    if (items[key]?.actions?.runValidation) {
                        validations[key] = async () => await items[key].actions?.runValidation(getValue())
                    }

                    parent[lastKey] = getValue()

                }

            }

            return a

        }, {})

    }

    async runValidations({ validations, errors = {} }) {
        if (Object.keys(validations).length) {
            let i = -1

            async function next() {
                i = i + 1
                if (Object.keys(validations)[i]) {
                    const key = Object.keys(validations)[i]
                    const res = await validations[key]()
                    if (res?.error) {
                        errors[key] = res
                    }
                    await next()
                }
            }

            await next()
        }
    }

    async onSubmit(e) {

        if (e) {
            e.preventDefault()
        }

        const progress = this.progress
        const done = this.done

        if (progress || done) {
            return null
        }

        this.progress = true

        if (this.submitRef.actions) {
            await this.submitRef.actions.setProgress(true)
        }

        const { successMessage } = this.props

        const {
            onSubmit = async function onSubmit(/*e, formData*/) {
                return new Promise(async function(resolve) {
                    return resolve(response)
                })
            }
        } = this.props

        const newState = { ...this.state, formData: { ...this.state.formData, ...this._formData } }
        newState.formData = { ...newState.formData }

        this._formData = {}
        await this.setState(newState)

        const items = this.items

        const {
            formData
        } = newState

        const validations = {}

        const sendData = this.getSendData({ formData, validations })

        const errors = {}

        await this.runValidations({ validations, errors })

        if (Object.keys(errors).length) {
            const refs = Object.keys(errors)
                .map((key) => items[key]?.actions?.getRef())
                .filter((e) => e)
                .sort((a, b) => {
                    const aTop = a.getBoundingClientRect().top || 0
                    const bTop = b.getBoundingClientRect().top || 0
                    return (aTop > bTop) ? 1 : (aTop < bTop) ? -1 : 0
                })

            const ref = refs.length ? refs[0] : null

            if (ref) {
                ref.scrollIntoView({ block: 'center', behavior: 'smooth' })
                //const y = ref.getBoundingClientRect().top + window.scrollY - 100 - 16
                //window.scrollTo({ top: Math.round(y), behavior: 'smooth' })
            }

            if (this.submitRef.actions) {
                await this.submitRef.actions.setProgress(false)
            }

            this.progress = false

            return
        }

        await this.props.appContext.template.current?.actions?.setLogoAnimation(1)

        const response = await onSubmit(e, sendData)

        await this.props.appContext.template.current?.actions?.setLogoAnimation(0)

        if ((response && response.error) || (response && response.errors)) {

            const message = (response.error?.message) || (response.errors && response.errors[0] && response.errors[0].message) || 'Error'
            const errors = response.error?.errors || response.errors

            let shouldSetState = false

            if (errors && errors.length) {

                Object.keys(formData).forEach(function(path) {
                    formData[path].errorMessage = ''
                    formData[path].error = false
                    if (formData[path].errors) {
                        formData[path].errors = []
                    }
                })

                errors.forEach(function(error) {

                    const message = error.message || ''
                    const path = Array.isArray(error.path) ? error.path.slice(1).join('.') : error.path
                    const dotParts = (typeof path === 'string' && path) ? path.split('.') : []

                    if (!isNaN(Number(dotParts[dotParts.length - 1])) && !formData[path]) {
                        const errorPath = Number(dotParts[dotParts.length - 1])
                        const formPath = path.split('.' + dotParts[dotParts.length - 1])[0]
                        if (formData[formPath]) {
                            if (!formData[formPath].errors) {
                                formData[formPath].errors = []
                            }
                            formData[formPath].errors.push({ path: errorPath, message })
                            formData[formPath].error = true
                            formData[formPath].errorMessage = message
                            formData[formPath].needErrorUpdate = Date.now()
                            shouldSetState = true
                        }
                    } else if (formData[path]) {
                        formData[path].errorMessage = message
                        formData[path].error = true
                        formData[path].needErrorUpdate = Date.now()
                        shouldSetState = true
                    }

                })
            }

            if (shouldSetState) {
                await this.setState(newState)
            }

            if (message) {
                await this.props.appContext.template.current?.actions?.setSnackMessage(message, 6000, 'error')
            }

            if (this.submitRef.actions) {
                await this.submitRef.actions.setProgress(false)
            }

            const firstErrorKey = Object.keys(formData).find((key) => formData[key].error && items[key]?.actions?.getRef)
            const ref = items[firstErrorKey]?.actions ? items[firstErrorKey].actions.getRef() : null

            if (ref) {
                ref.scrollIntoView({ block: 'center', behavior: 'smooth' })
                //const y = ref.getBoundingClientRect().top + window.scrollY - 100 - 16
                //window.scrollTo({ top: y, behavior: 'smooth' })
            }

            await new Promise((resolve) => setTimeout(resolve, 1000))
            this.progress = false

        } else {

            this.done = true
            this.progress = false

            this.removeLeaveMessageListener()

            if (!this.isUnmounted) {

                if (this.submitRef.actions) {
                    await this.submitRef.actions.setProgress(false)
                    await this.submitRef.actions.setDisabled(true)
                    await this.submitRef.actions.setDone(true)
                }

                if (response) {

                    if (successMessage) {
                        await this.props.appContext.template.current?.actions?.setSnackMessage(successMessage)
                    }

                }

            }

        }

        return response

    }

    async setNewFormData(formData, runValidations) {
        this._formData = {}
        const newState = {
            formData: {
                ...copyObject(formData, { keep: ['label', 'children', 'startIcon', 'endIcon', 'onClick', 'Component', 'LabelProps', 'LabelComponent', 'afterChildren', 'NewComponentProps'] }) || {}
            }
        }
        await this.setState(newState)

        if (runValidations) {

            const t = this

            async function asyncFunction() {

                const validations = {}
                t.getSendData({ formData: newState.formData, validations })

                const items = t.items

                const errors = {}

                await t.runValidations({ validations, errors })

                if (Object.keys(errors).length) {
                    const refs = Object.keys(errors)
                        .map((key) => items[key]?.actions?.getRef())
                        .filter((e) => e)
                        .sort((a, b) => {
                            const aTop = a.getBoundingClientRect().top || 0
                            const bTop = b.getBoundingClientRect().top || 0
                            return (aTop > bTop) ? 1 : (aTop < bTop) ? -1 : 0
                        })

                    const ref = refs.length ? refs[0] : null

                    if (ref) {
                        ref.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }

                }

            }

            asyncFunction()

        }


    }

    render() {

        const onSubmit = this.onSubmit
        const onChange = this.onChange

        const {
            formData
        } = this.state

        const {
            className,
            components = defaultComponents,
            postContext,
            accountContext,
            appContext,
            size = 'large',
            isQuery,
            showInitializationErrors = postContext.post?._id
        } = this.props

        const user = postContext.user || accountContext.user
        const post = postContext.post || accountContext.user
        const author = post?._author?._id || post?._author
        const isAuthor = (user?._id && user?._id === author)
        const isAdmin = user && user._status_isFeatured
        const isAuthorOrAdmin = !!(isAuthor || isAdmin)

        const name = postContext.parentRoute ? postContext.name : accountContext.name

        const root = {
            key: '',
            items: {}
        }

        Object.keys(formData).sort((a, b) => {
            const aO = formData[a].order || 100
            const bO = formData[b].order || 100
            if (a === 'submit') {
                return 1
            }
            return (aO > bO) ? 1 : (aO < bO) ? -1 : 0
        }).forEach((key) => {

                const dotParts = (formData[key].containerKey || key).split('.')

                if (dotParts.length) {

                    dotParts.reduce((o, part, i) => {

                        if (!o[part] && i !== dotParts.length - 1) {
                            o[part] = {
                                propKey: dotParts.slice(0, i + 1).join('.'),
                                label: appContext.labels[name + 'ContainerLabel_' + dotParts.slice(0, i + 1).join('_')],
                                items: {}
                            }
                        } else if (i === dotParts.length - 1) {
                            if (!formData[key].hidden) {
                                if (!formData[key].isContainer) {
                                    o[part] = {
                                        propKey: key,
                                        ...formData[key]
                                    }
                                } else {
                                    o[part] = {
                                        propKey: key,
                                        label: appContext.labels[name + 'ContainerLabel_' + dotParts.slice(0, i + 1).join('_')],
                                        items: {},
                                        ...o[part] ? o[part] : {},
                                        ...formData[key]
                                    }
                                }
                            }
                        }

                        return (o[part] && o[part].items) ? o[part].items : o[part]

                    }, root.items)
                }
            }
        )

        const formActions = {
            reEnable: async () => {
                if (!this.progress) {
                    this.done = false
                    if (this.submitRef.actions) {
                        await this.submitRef.actions.setDisabled(false)
                        await this.submitRef.actions.setDone(false)
                    }
                }
            }
        }

        const submitRef = this.submitRef

        const items = this.items

        return (
            <div className={clsx(
                style.formContainer,
                className
            )}>
                <form className={style.form}
                      autoComplete={'off'}
                      noValidate
                      onSubmit={onSubmit}
                >
                    <Container
                        container={root}
                        commonProps={{
                            formData,
                            components,
                            onSubmit,
                            onChange,
                            submitRef,
                            isAuthorOrAdmin,
                            isAdmin,
                            post,
                            size,
                            formActions,
                            messages: appContext.messages,
                            name,
                            showInitializationErrors,
                            setItem: (name, e) => {
                                items[name] = e
                            },
                            getItem: (name) => {
                                return items[name]
                            },
                            isQuery
                        }}
                    />
                </form>
            </div>
        )
    }
}

Form.contextType = WappContext

const FormWithContext = React.forwardRef((props, ref) => {

    const postContext = useContext(PostContext)
    const accountContext = useContext(AccountContext)
    const appContext = useContext(AppContext)

    return (
        <Form {...props} postContext={postContext} accountContext={accountContext} appContext={appContext} ref={ref} />
    )
})

export default withWapp(FormWithContext)
